import React, { useState, useEffect, useRef } from "react";
import {
  SearchIcon,
  UploadIcon,
  DownloadIcon,
  DownloadAllIcon,
  TrashIcon,
  Document2Icon,
  UserIconV2,
  ArrowDownIcon,
  ArrowDownBlackIcon,
} from "~/assets/icons";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import jobDetailsApi, { IJobDocument } from "~/apis/jobDetails";
import { notify } from "~/utils/helper";
import UploadDocumentModal from "./UploadDocumentModal";
import styles from "./DocumentTab.module.scss";
import classNames from "classnames/bind";
const cx = classNames.bind(styles);

const getViewUrl = (fileUrl: string, fileType: string): string => {
  const officeTypes = ["WORD", "EXCEL", "DOC", "DOCX", "XLS", "XLSX"];
  if (officeTypes.includes(fileType.toUpperCase())) {
    return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=false`;
  }
  return fileUrl;
};

interface Document {
  id: string;
  name: string;
  postedOn: string;
  postedTime: string;
  type: string;
  size: string;
  uploadedBy: string;
  fileUrl?: string;
  description?: string;
}

const DocumentTab = () => {
  const jobId = window.location.pathname.split("/").pop() || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("All Types");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPage: 0,
  });
  const isInitialMount = useRef(true);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const documentTypes = ["All Types", "PDF", "EXCEL", "WORD"];

  // Format file size
  const formatFileSize = (size: string | number): string => {
    const sizeNum = typeof size === "string" ? parseInt(size) : size;
    if (sizeNum >= 1024 * 1024) {
      return `${(sizeNum / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (sizeNum >= 1024) {
      return `${(sizeNum / 1024).toFixed(2)} KB`;
    }
    return `${sizeNum} B`;
  };

  // Format date
  const formatDate = (dateString: string): { date: string; time: string } => {
    const date = new Date(dateString);
    const dateStr = date.toISOString().split("T")[0];
    const timeStr = date.toTimeString().split(" ")[0].slice(0, 5);
    return { date: dateStr, time: timeStr };
  };

  // Convert API response to Document format
  const convertToDocument = (doc: IJobDocument): Document => {
    const { date, time } = formatDate(doc.createdAt);
    return {
      id: doc.id.toString(),
      name: doc.fileName,
      postedOn: date,
      postedTime: time,
      type: doc.fileType,
      size: formatFileSize(doc.fileSize),
      uploadedBy: doc.uploadedBy,
      fileUrl: doc.file?.url,
      description: doc.description,
    };
  };

  // Load documents from API
  const loadDocuments = async () => {
    if (!jobId) return;

    try {
      setIsLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (searchQuery) {
        params.textSearch = searchQuery;
      }

      if (selectedType !== "All Types") {
        params.fileType = selectedType;
      }

      const response = await jobDetailsApi.getJobDocuments(jobId, params);
      if (response.data.status === "success" && response.data.data) {
        const convertedDocs = response.data.data.responses.map(convertToDocument);
        setDocuments(convertedDocs);
        setPagination({
          page: response.data.data.pagination.page,
          limit: response.data.data.pagination.limit,
          totalItems: response.data.data.pagination.totalItems,
          totalPage: response.data.data.pagination.totalPage,
        });
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset page to 1 when search or filter changes
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedType]);

  // Load documents - single source of truth
  useEffect(() => {
    if (!jobId) return;

    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Debounce only for search/filter changes, not for initial load or page change
    const shouldDebounce = !isInitialMount.current && (searchQuery || selectedType !== "All Types");
    const delay = shouldDebounce ? 500 : 0;

    const timer = setTimeout(() => {
      loadDocuments();
      isInitialMount.current = false;
    }, delay);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, pagination.page, searchQuery, selectedType]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleDownload = async (document: Document) => {
    if (!jobId) {
      notify("error", "Job ID not found");
      return;
    }

    if (!document.id) {
      notify("error", "Document ID not available");
      return;
    }

    try {
      const response = await jobDetailsApi.downloadJobDocument(jobId, document.id);

      // Create blob from response
      const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = document.name || "document";
      window.document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);

      notify("success", "Document downloaded successfully");
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to download document");
    }
  };

  const handleDownloadAll = async () => {
    if (!jobId) {
      notify("error", "Job ID not found");
      return;
    }

    try {
      setIsDownloadingAll(true);
      const response = await jobDetailsApi.downloadAllJobDocuments(jobId);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/zip' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-${jobId}-documents.zip`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      notify("success", "All documents downloaded successfully");
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to download all documents");
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleUpload = () => {
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    loadDocuments();
  };

  const categoryOptions = [
    { id: "Insurance Scope", label: "Insurance Scope" },
    { id: "Estimate", label: "Estimate" },
    { id: "Aerial", label: "Aerial" },
    { id: "Invoice", label: "Invoice" },
    { id: "Material Order", label: "Material Order" },
    { id: "Work Order", label: "Work Order" },
    { id: "Other Document", label: "Other Document" },
  ];

  const visibilityOptions = [
    { id: "Public", label: "Public" },
    { id: "Private", label: "Private" },
  ];

  const handleDeleteClick = (document: Document) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      await jobDetailsApi.deleteJobDocument(documentToDelete.id);
      notify("success", "Document deleted successfully");
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      // Reload documents list
      loadDocuments();
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to delete document");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  // No need for client-side filtering since API handles it
  const filteredDocuments = documents;

  return (
    <div className="w-full h-full flex flex-col bg-[#071427] text-white">
      {/* Top Bar */}
      <div className="flex items-center justify-between p-4 border-b border-[#111e33]">
        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A2A7B4]" />
          <input
            type="text"
            placeholder="Search documents"
            value={searchQuery}
            onChange={handleSearch}
            className="w-[300px] pl-10 pr-4 py-2 bg-[#071427] border border-[#1f2937] rounded-[27px] text-[#949699] placeholder-[#A2A7B4] focus:outline-none focus:border-[#3b82f6]"
          />
        </div>

        {/* Right Side Buttons */}
        <div className="flex items-center gap-4">
          {/* Type Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-[#D9D9D9] text-[#000000] text-[14px] rounded-[27px] hover:bg-[#C9C9C9] transition-colors"
            >
              <span className="text-[14px] text-[#000000]">{selectedType}</span>
              <ArrowDownBlackIcon className="w-2 h-1" />
            </button>
            {showTypeDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowTypeDropdown(false)}
                />
                <div className="absolute top-full mt-2 right-0 z-20 bg-[#0C192D] border border-[#1f2937] rounded-lg shadow-lg min-w-[150px]">
                  {documentTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setSelectedType(type);
                        setShowTypeDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-[#1f2937] transition-colors ${selectedType === type
                        ? "bg-[#1f2937] text-[#3b82f6]"
                        : "text-white"
                        }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Download All Button */}
          <PrimaryButton
            title="Download All"
            icon={<DownloadAllIcon className="w-4 h-4" />}
            onClick={handleDownloadAll}
            disabled={isDownloadingAll}
            loading={isDownloadingAll}
            className="!bg-[#3b82f6] !text-white hover:!bg-[#2563eb] !rounded-[27px]"
          />

          {/* Upload Button */}
          <PrimaryButton
            title="Upload"
            icon={<UploadIcon className="w-4 h-4" />}
            onClick={handleUpload}
            className="!bg-[#3b82f6] !text-white hover:!bg-[#2563eb] !rounded-[27px]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-[10px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-[#A2A7B4]">Loading documents...</p>
          </div>
        ) : (
          <div className={cx("table-wrapper")}>
            <table className="w-full">
              <thead className="bg-[#0E264C] sticky top-0 z-10">
                <tr>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] pl-5">
                    Document
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Posted On
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Type
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Size
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] min-w-[160px]">
                    Description
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Uploaded By
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] pr-5">
                    Actions
                  </th>
                </tr>
                <tr>
                  <td colSpan={7} className="px-0">
                    <div className="mx-5 border-b border-[#111e33]"></div>
                  </td>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-[#A2A7B4]">
                      No documents found
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((document) => (
                    <React.Fragment key={document.id}>
                      <tr
                        className="hover:bg-[#0a1b34] transition-colors"
                      >
                        <td className="px-4 py-4 pl-5">
                          <button
                            className="flex items-center gap-3 text-left group"
                            onClick={() => document.fileUrl ? window.open(getViewUrl(document.fileUrl, document.type), "_blank", "noopener,noreferrer") : undefined}
                            disabled={!document.fileUrl}
                            title={document.fileUrl ? "Click to view" : document.name}
                          >
                            <Document2Icon className="w-5 h-5 text-[#3b82f6] flex-shrink-0" />
                            <div className="flex flex-col min-w-0">
                              <span
                                className={`text-sm truncate max-w-[220px] ${document.fileUrl
                                  ? "text-white group-hover:text-[#3b82f6] transition-colors cursor-pointer underline-offset-2 hover:underline"
                                  : "text-white"
                                  }`}
                              >
                                {document.name}
                              </span>
                              {document.description ? (
                                <span
                                  className="text-xs text-[#A2A7B4] truncate max-w-[220px] mt-0.5"
                                  title={document.description}
                                >
                                  {document.description}
                                </span>
                              ) : null}
                            </div>
                          </button>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-sm text-white">{document.postedOn}</span>
                            <span className="text-xs text-[#A2A7B4]">{document.postedTime}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-[#949699]">{document.type}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-[#949699] whitespace-nowrap">{document.size}</span>
                        </td>
                        <td className="px-4 py-4 min-w-[160px]">
                          <span
                            className="text-sm text-[#949699] truncate max-w-[240px] block"
                            title={document.description || undefined}
                          >
                            {document.description?.trim() ? document.description : "—"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <UserIconV2 className="w-4 h-4" />
                            <span className="text-sm text-[#949699]">{document.uploadedBy}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 pr-5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownload(document)}
                              className="p-2 bg-[#0C192D] border border-[#1f2937] rounded hover:bg-[#1f2937] transition-colors"
                              title="Download"
                            >
                              <DownloadIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(document)}
                              className="p-2 bg-[#0C192D] border border-[#1f2937] rounded hover:bg-[#1f2937] transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={7} className="px-0 py-0">
                          <div className="mx-5 border-b border-[#111e33]"></div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && documentToDelete && (
        <ModalAlertDelete
          open={showDeleteModal}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          description={`Are you sure you want to delete "${documentToDelete.name}"? This action cannot be undone.`}
        />
      )}

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={handleUploadSuccess}
        jobId={jobId}
        categoryOptions={categoryOptions}
        visibilityOptions={visibilityOptions}
      />
    </div>
  );
};

export default DocumentTab;
